import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { http, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Shield, ArrowLeft, KeyRound, AlertTriangle } from "lucide-react";
import PasswordInput from "@/components/PasswordInput";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    if (pw.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setBusy(true);
    try {
      await http.post("/auth/reset-password", { token, new_password: pw });
      toast.success("Password updated. Please sign in.");
      nav("/login", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center p-6">
        <div className="bento-card max-w-md p-8 text-center space-y-4" data-testid="reset-missing-token">
          <AlertTriangle size={28} className="mx-auto text-amber-300" />
          <h1 className="font-display text-2xl font-black uppercase tracking-tight">
            Reset link <span className="text-electric">incomplete</span>
          </h1>
          <p className="text-white/50 text-sm">
            This link is missing its security token. Request a new reset link to continue.
          </p>
          <Link to="/forgot-password" className="btn-primary justify-center" data-testid="reset-request-new">
            Request new link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-64px)] flex items-center justify-center p-6 md:p-12">
      <div className="w-full max-w-md" data-testid="reset-password-page">
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-xs text-white/60 hover:text-white mb-6"
        >
          <ArrowLeft size={14} /> Back to sign in
        </Link>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-md bg-electric flex items-center justify-center">
            <Shield size={20} className="text-black" />
          </div>
          <div className="label-mono">VERIFIED · New Password</div>
        </div>

        <h1 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tight">
          Pick a new <span className="text-electric">password</span>.
        </h1>
        <p className="text-white/50 text-sm mt-3">
          Choose something at least 8 characters long. We'll sign you in after you reset.
        </p>

        <form onSubmit={submit} className="mt-8 space-y-4" data-testid="reset-form">
          <div>
            <label className="label-mono block mb-2">New password</label>
            <PasswordInput
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              className="input-dark w-full rounded-md px-4 py-3 text-sm"
              placeholder="••••••••"
              testId="reset-pw-input"
            />
          </div>
          <div>
            <label className="label-mono block mb-2">Confirm password</label>
            <PasswordInput
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              className="input-dark w-full rounded-md px-4 py-3 text-sm"
              placeholder="••••••••"
              testId="reset-pw-confirm"
            />
          </div>
          <button
            type="submit"
            disabled={busy}
            className="btn-primary w-full justify-center disabled:opacity-50"
            data-testid="reset-submit-btn"
          >
            <KeyRound size={14} /> {busy ? "Updating…" : "Update password"}
          </button>
        </form>
      </div>
    </div>
  );
}
